import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import { type EmploymentFormSchema, employmentFormSchema } from "@msuite/utils/schemas";
import { getActiveEmployment } from "@msuite/utils/users";
import { FormProvider, useForm } from "react-hook-form";
import { EmploymentForm } from "@/forms/employment-form";
import { client } from "@/hono";
import { fetchUserEmployments } from "@/hooks/use-user-employments";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface AddEmploymentModalProps extends ModalProps {
	userId: string;
}

export function AddEmploymentModal({ isOpen, onClose, userId }: AddEmploymentModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const data = await fetchUserEmployments(userId);
		if (!data) return {} as EmploymentFormSchema;
		const source = getActiveEmployment(data.employments);
		if (!source) return {} as EmploymentFormSchema;
		return {
			jobTitle: source.jobTitle,
			validFrom: source.validTo ? moment(source.validTo).add(1, "day").db() : undefined,
			workdays: source.workdays,
			hoursPerWeek: source.hoursPerWeek,
			vacationDaysAccrualType:
				source.vacationDaysAccrualType as EmploymentFormSchema["vacationDaysAccrualType"],
			vacationDays: source.vacationDays,
		} as EmploymentFormSchema;
	});
	const methods = useForm({
		resolver: resolver(employmentFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: EmploymentFormSchema) {
		const response = await client.users[":userId"].employments.$post({ param: { userId }, json });
		await responseHandler(response);
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("personnel.employment.add_employment")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<EmploymentForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
